<template>
  <div>
    <PageHeader
      title="Funnel"
      subtitle="Prospects you are talking to: what has happened, where it stands, and what to do next."
      :heading-level="1"
    />

    <div class="px-6 py-4">
      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div class="flex flex-wrap items-start gap-4 px-4 py-3 border-b border-gray-100">
          <FilterTabs label="Momentum" :model-value="momentumFilter" :options="momentumOptions" @update:model-value="momentumFilter = $event" />
          <FilterTabs label="Stage read" :model-value="stageFilter" :options="stageOptions" @update:model-value="stageFilter = $event" />
          <label class="inline-flex flex-col gap-0.5 flex-1 min-w-[200px] max-w-sm">
            <span class="text-xs font-semibold">Search</span>
            <input v-model="search" type="text" placeholder="Practice or owner" class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </label>
        </div>
        <div class="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
          <span><span class="font-semibold text-gray-900">{{ filtered.length }}</span> shown</span>
          <span><span class="font-semibold text-gray-900">{{ rows.length }}</span> prospects with engagements</span>
          <span><span class="font-semibold text-gray-900">{{ withNext }}</span> with a meeting scheduled</span>
          <span><span class="font-semibold text-gray-900">{{ withIntel }}</span> with intelligence</span>
        </div>
        <LoadingIndicator v-if="loading" message="Loading funnel..." />
        <div v-else-if="!rows.length" class="px-4 py-12 text-center">
          <div class="text-sm font-semibold text-gray-900">No engagements yet</div>
          <p class="text-sm text-gray-600 mt-1">Practices appear in the funnel once Salesforce has activities on a matched account.</p>
        </div>
        <AgGridVue
          v-else
          class="pursuits-grid"
          :theme="gridTheme"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :row-data="filtered"
          :row-height="56"
          :header-height="40"
          dom-layout="autoHeight"
          :suppress-cell-focus="true"
          :get-row-id="(p) => String(p.data.id)"
          row-class="cursor-pointer"
          @row-clicked="$router.push({ name: 'Entity', params: { entity_id: $event.data.id } })"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { themeQuartz } from 'ag-grid-community'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

export const STAGE_LABEL = {
  not_started: 'Not started', early: 'Early', engaged: 'Engaged', evaluating: 'Evaluating',
  negotiating: 'Negotiating', stalled: 'Stalled', won: 'Won', lost: 'Lost',
}
export const MOMENTUM_DOT = { warming: 'bg-secondary-800', steady: 'bg-neutral-700', cooling: 'bg-yellow-dark', unknown: 'bg-gray-300' }
export const MOMENTUM_LABEL = { warming: 'Warming', steady: 'Steady', cooling: 'Cooling', unknown: 'Unknown' }

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''))
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export default {
  name: 'PursuitsTab',
  components: { AgGridVue, PageHeader, FilterTabs, LoadingIndicator },
  emits: [],
  data() {
    return {
      loading: true,
      rows: [],
      momentumFilter: 'all',
      momentumOptions: [
        { value: 'all', label: 'All' }, { value: 'warming', label: 'Warming' }, { value: 'steady', label: 'Steady' }, { value: 'cooling', label: 'Cooling' },
      ],
      stageFilter: 'all',
      stageOptions: [
        { value: 'all', label: 'All' }, { value: 'early', label: 'Early' }, { value: 'engaged', label: 'Engaged' },
        { value: 'evaluating', label: 'Evaluating' }, { value: 'negotiating', label: 'Negotiating' }, { value: 'stalled', label: 'Stalled' },
      ],
      search: '',
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light', headerBackgroundColor: '#F7F7F8', headerTextColor: '#626264', headerFontSize: 12, headerFontWeight: 600,
        fontSize: 13, rowBorder: true, wrapperBorder: false, wrapperBorderRadius: 0, cellHorizontalPadding: 16, rowHoverColor: '#F7F7F8',
      }),
      defaultColDef: { sortable: true, resizable: false, suppressMovable: true, suppressHeaderMenuButton: true, suppressHeaderFilterButton: true, cellClass: 'cell-theme-left-aligned' },
      columnDefs: [
        {
          headerName: 'Practice', field: 'name', flex: 2, minWidth: 220,
          cellRenderer: (p) => `<div class="leading-tight"><div class="font-semibold text-gray-900"><span class="sensitive">${escapeHtml(p.data.name)}</span><span class="inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold bg-blue-50 text-blue-700 ml-1.5 align-text-bottom" title="Salesforce ${p.data.kind}">S</span></div><div class="text-[11px] text-gray-500 sensitive">${escapeHtml([p.data.city, p.data.state].filter(Boolean).join(', '))}${p.data.owner_name ? ' · ' + escapeHtml(p.data.owner_name) : ''}</div></div>`,
        },
        { headerName: 'Salesforce', field: 'status', width: 140, cellClass: 'cell-theme-left-aligned sensitive' },
        {
          headerName: 'Momentum', field: 'momentum', width: 120,
          cellRenderer: (p) => (p.value ? `<span class="inline-flex items-center gap-2 font-medium text-gray-900"><span class="inline-block w-2 h-2 rounded-sm ${MOMENTUM_DOT[p.value] || 'bg-gray-300'}"></span>${MOMENTUM_LABEL[p.value] || p.value}</span>` : '<span class="text-gray-400">Not read yet</span>'),
        },
        { headerName: 'Stage read', field: 'stage_read', width: 120, valueFormatter: (p) => STAGE_LABEL[p.value] || '' },
        { headerName: 'Touches', field: 'engagements', width: 90, type: 'numericColumn', cellClass: 'cell-theme-right-aligned tabular-nums' },
        { headerName: 'Last touch', field: 'last_touch', width: 110, valueFormatter: (p) => fmtDate(p.value) },
        {
          headerName: 'Next meeting', field: 'next_meeting', width: 170,
          cellRenderer: (p) => (p.value ? `<div class="leading-tight min-w-0 w-full"><div>${fmtDate(p.value.date)}</div><div class="text-[11px] text-gray-500 sensitive truncate" title="${escapeHtml(p.value.subject)}">${escapeHtml(p.value.subject)}</div></div>` : '<span class="text-gray-400">None</span>'),
        },
        {
          headerName: 'Next objective', field: 'next_objective', flex: 3, minWidth: 260,
          cellClass: 'cell-theme-left-aligned sensitive',
          cellRenderer: (p) => (p.value ? `<div class="line-clamp-2 leading-tight" title="${escapeHtml(p.value)}">${escapeHtml(p.value)}</div>` : '<span class="text-gray-400">Run intelligence to get one</span>'),
        },
        {
          headerName: '', field: 'id', width: 48, sortable: false, cellClass: 'cell-theme-center-aligned',
          cellRenderer: () => '<span class="text-gray-400" title="Open"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.5 6 6l-4.5 4.5"/></svg></span>',
        },
      ],
    }
  },
  computed: {
    withNext() {
      return this.rows.filter((r) => r.next_meeting).length
    },
    withIntel() {
      return this.rows.filter((r) => r.momentum).length
    },
    filtered() {
      const q = this.search.trim().toLowerCase()
      return this.rows.filter((r) => {
        if (this.momentumFilter !== 'all' && r.momentum !== this.momentumFilter) return false
        if (this.stageFilter !== 'all' && r.stage_read !== this.stageFilter) return false
        if (q && !(r.name || '').toLowerCase().includes(q) && !(r.owner_name || '').toLowerCase().includes(q)) return false
        return true
      })
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await this.$axios.get('/api/growth/pursuits/')
        this.rows = data.rows
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.pursuits-grid :deep(.ag-center-cols-viewport) {
  min-height: 0 !important;
}
.pursuits-grid :deep(.cell-theme-right-aligned) {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end;
}
</style>
